import { gql } from "utils/urql";
import cx from "classnames";
import { ArticleBentoRecircFragment } from "types/api";
import Image from "components/Image";
import styles from "./ArticleBentoRecirc.module.scss";

const ArticleBentoRecirc = ({
  displayName,
  url,
  river,
  presentedByUrl,
  presentedByDisplayName,
  accentColor,
  textColor,
}: ArticleBentoRecircFragment) => {
  const colorReverse = textColor === "#ffffff";
  return (
    <aside
      className={styles.root}
      style={{ backgroundColor: `${accentColor}`, color: `${textColor}` }}
      aria-labelledby="more-in-series-heading"
      data-category="story page"
    >
      <div className={styles.intro}>
        <div
          className={cx(
            styles.headingContainer,
            colorReverse && styles.borderColorReverse
          )}
        >
          <h2 className={styles.desktopHeading} id="more-in-series-heading">
            <a
              data-action={`click link - ${displayName.toLowerCase()} - more from`}
              href={url}
            >
              More From {displayName}
            </a>
          </h2>
          <h2 className={styles.mobileHeading}>More From {displayName}</h2>
          {presentedByDisplayName && (
            <span className={styles.sponsor}>
              {`presented by `}
              {presentedByUrl ? (
                <a
                  href={presentedByUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.sponsorName}
                >
                  <span className={styles.sponsorName}>
                    {presentedByDisplayName}
                  </span>
                </a>
              ) : (
                <span className={styles.sponsorName}>
                  {presentedByDisplayName}
                </span>
              )}
            </span>
          )}
        </div>
        <a
          href={url}
          className={cx(
            styles.button,
            colorReverse && styles.buttonColorReverse
          )}
        >
          Explore This Series
        </a>
      </div>
      <ul className={styles.articles}>
        {river.edges.map((edge, i) => {
          return (
            <li
              key={`article-bento-recirc-${i}`}
              className={cx(styles.article, i === 3 && styles.hideAtBreak)}
            >
              <a href={edge.node.url} className={styles.articleLink}>
                {edge.node.riverImage && (
                  <figure
                    className={styles.articleFigure}
                    data-action={`click link - project - image ${i + 1}`}
                    data-label={edge.node.url}
                  >
                    <picture className={styles.articlePicture}>
                      <Image
                        src={edge.node.riverImage.url}
                        srcSet={edge.node.riverImage.srcSet || undefined}
                        width={edge.node.riverImage.width}
                        height={edge.node.riverImage.height}
                        alt={edge.node.riverImage.altText || ""}
                        lazy={true}
                        className={styles.articleImage}
                      />
                    </picture>
                  </figure>
                )}
                <h3
                  className={styles.articleTitle}
                  dangerouslySetInnerHTML={{ __html: edge.node.title }}
                  data-action={`click link - project - title ${i + 1}`}
                  data-label={edge.node.url}
                ></h3>
              </a>
              {edge.node.authors.map((author) => {
                return (
                  <a
                    key={author.slug}
                    className={styles.articleAuthor}
                    href={author.url}
                    data-action={`click link - project - author ${i + 1}`}
                    data-label={author.url}
                  >
                    {author.displayName}
                  </a>
                );
              })}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default ArticleBentoRecirc;

ArticleBentoRecirc.fragment = gql`
  fragment articleRecircAuthor on Author {
    url
    displayName
    id
    slug
  }

  fragment articleRecirc on RiverConnection {
    edges {
      node {
        url
        title
        authors {
          ...articleRecircAuthor
        }
        riverImage(width: 80, height: 80, sizes: ["1x", "2x"]) {
          id
          url
          srcSet
          width
          height
          altText
        }
      }
    }
  }

  fragment articleBentoRecircSwan on EditorialProject {
    displayName
    url
    accentColor
    textColor
    presentedByUrl
    presentedByDisplayName
    river(first: 4, excludes: [$id]) {
      ...articleRecirc
    }
  }
`;
